import axios, {
  AxiosError,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios'

import getSession from '../actions/auth/get-session'
import fetchRefreshToken from '../actions/auth/refresh-token'
import setSession from '../actions/auth/set-session'
import { getEncryptedCookies } from '../actions/encrypt-decrypt-cookies'
import { getIPAddress } from '../actions/get-ip-address'
import redirectToLogout from '../actions/redirect-to-logout'
import suspendAccount from '../actions/suspend-account'
import { TApiResult } from '../types/global.types'

import { openReplayTracker } from '@/components/private/openreplay'
import getDeviceGUID from '@/libs/actions/get-device-guid'

interface RetryQueueItem {
  resolve: (value?: any) => void
  reject: (error?: any) => void
  config: AxiosRequestConfig
}

const failedQueue: RetryQueueItem[] = []
let isRefreshing = false

export const setRequestHeaders = async (request: InternalAxiosRequestConfig) => {
  const headersAttributes = await getEncryptedCookies()
  const deviceGUID = await getDeviceGUID()
  const ip = await getIPAddress()

  if (ip) {
    request.headers['x-forwarded-for'] = ip
  }

  if (headersAttributes?.deviceType) {
    request.headers['x-device-type'] = headersAttributes?.deviceType
  }

  if (deviceGUID) {
    request.headers['x-device-guid'] = deviceGUID
  }

  if (headersAttributes?.partnerId) {
    request.headers['x-partner-id'] = headersAttributes?.partnerId
  }

  if (headersAttributes?.isPir) {
    request.headers['x-partner-type'] = 'pir'
  }

  if (headersAttributes?.isPnir) {
    request.headers['x-partner-type'] = 'pnir'
  }

  if (typeof window === 'undefined') {
    request.headers['user-agent'] = headersAttributes?.userAgent
  }

  if (typeof window !== 'undefined') {
    const userType = headersAttributes?.isPir ? 'PIR' : headersAttributes?.isPnir ? 'PNIR' : 'NPR'
    openReplayTracker.setMetadata('userType', userType)
  }

  return request
}

const onRequestError = (error: AxiosError) => Promise.reject(error)

const onRequest = async (request: InternalAxiosRequestConfig) => {
  const session = await getSession()

  if (session) {
    const { accessToken } = session

    request.headers.Authorization = `Bearer ${accessToken}`
  }

  return await setRequestHeaders(request)
}

const onResponse = async (response: AxiosResponse) => {
  return response
}

const onResponseError = async (error: AxiosError<TApiResult>) => {
  const originalRequest = error.config as AxiosRequestConfig

  if (error?.response?.data?.traceId && typeof window !== 'undefined') {
    openReplayTracker.setMetadata('traceId', error?.response?.data?.traceId)
  }

  if (error.response && error.response?.status === 401) {
    return handleRefreshToken(originalRequest)
  } else if (error.response && error.response?.status === 403) {
    suspendAccount()
    return Promise.reject(error)
  } else {
    return Promise.reject(error)
  }
}

const handleRefreshToken = async (originalRequest: AxiosRequestConfig) => {
  if (!isRefreshing) {
    isRefreshing = true

    try {
      const session = await getSession()

      if (session) {
        let { accessToken, refreshToken } = session

        const response = await fetchRefreshToken(accessToken, refreshToken)

        if (response?.success) {
          accessToken = response?.result?.accessToken
          refreshToken = response?.result?.refreshToken
          await setSession({
            ...session,
            username: session.username,
            accessToken,
            refreshToken,
            partnerId: session?.partnerId ?? undefined,
          })

          if (originalRequest.headers) {
            originalRequest.headers['Authorization'] = `Bearer ${accessToken}`
          }

          failedQueue.forEach(({ config, resolve, reject }) => {
            axiosInstance
              .request(config)
              .then((response) => resolve(response))
              .catch((err) => reject(err))
          })

          failedQueue.length = 0

          return axios(originalRequest)
        } else {
          await redirectToLogout()
        }
      } else {
        await redirectToLogout()
      }
    } catch (error) {
      await redirectToLogout()
    } finally {
      isRefreshing = false
    }
  } else {
    return new Promise<void>((resolve, reject) => {
      failedQueue.push({ config: originalRequest, resolve, reject })
    })
  }
}

const axiosInstance = axios.create({
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json, text/plain, */*',
  },
})

axiosInstance.interceptors.request.use(onRequest, onRequestError)
axiosInstance.interceptors.response.use(onResponse, onResponseError)

export default axiosInstance
